import * as React from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import BottomSheetContent from './Content';

// Movement (px) before a press on the sheet header turns into a drag
const DRAG_THRESHOLD = 8;
// Distance (px) that is enough to flip the sheet regardless of where it is released
const FLICK_DISTANCE = 48;
// Swipe-up hint: first nudge after load, then a pause between nudges, until the user opens the sheet
const HINT_FIRST_DELAY = 1200;
const HINT_REPEAT_DELAY = 6000;
const HINT_MAX_RUNS = 3;

type DragState = {
  pointerId: number,
  startY: number,
  startOffset: number,
  closedOffset: number,
  dragging: boolean,
};

const BottomSheet = (): JSX.Element => {
  const intl = useIntl();
  const { isMobileView } = useSelector((state: CR.RootState) => state.settings);
  // On wide screens the sheet becomes a floating side panel that is always expanded
  const isPanel = isMobileView === 'false';

  const [open, setOpen] = React.useState(false);
  const [peekHeight, setPeekHeight] = React.useState(0);
  const [dragOffset, setDragOffset] = React.useState<number | null>(null);
  const [hinting, setHinting] = React.useState(false);
  const [hintRuns, setHintRuns] = React.useState(0);
  const [hintDone, setHintDone] = React.useState(false);

  const sheetRef = React.useRef<HTMLDivElement>(null);
  const peekRef = React.useRef<HTMLDivElement>(null);
  const drag = React.useRef<DragState | null>(null);

  // Keep the collapsed height in sync with the summary content (it changes as the order updates)
  React.useLayoutEffect(() => {
    const peek = peekRef.current;
    if (!peek) return undefined;

    setPeekHeight(peek.offsetHeight);

    const observer = new ResizeObserver(() => setPeekHeight(peek.offsetHeight));
    observer.observe(peek);

    return () => observer.disconnect();
  }, []);

  const isOpen = isPanel || open;

  // Once the user has opened the sheet they know it is there
  React.useEffect(() => {
    if (open) {
      setHintDone(true);
      setHinting(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (isPanel || open || hintDone || hinting || hintRuns >= HINT_MAX_RUNS || peekHeight === 0) {
      return undefined;
    }

    const timer = window.setTimeout(
      () => setHinting(true),
      hintRuns === 0 ? HINT_FIRST_DELAY : HINT_REPEAT_DELAY,
    );

    return () => window.clearTimeout(timer);
  }, [isPanel, open, hintDone, hinting, hintRuns, peekHeight]);

  const onHintEnd = (e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    setHinting(false);
    setHintRuns((runs) => runs + 1);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isPanel || !e.isPrimary || !sheetRef.current || !peekRef.current) return;

    const closedOffset = sheetRef.current.offsetHeight - peekRef.current.offsetHeight;

    setHintDone(true);
    setHinting(false);

    drag.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      startOffset: open ? 0 : closedOffset,
      closedOffset,
      dragging: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (!state || state.pointerId !== e.pointerId) return;

    const dy = e.clientY - state.startY;

    if (!state.dragging) {
      if (Math.abs(dy) < DRAG_THRESHOLD) return;
      // Capture only once it is a real drag, so taps still reach the call button
      state.dragging = true;
      e.currentTarget.setPointerCapture(e.pointerId);
    }

    setDragOffset(Math.min(Math.max(state.startOffset + dy, 0), state.closedOffset));
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (!state || state.pointerId !== e.pointerId) return;

    drag.current = null;
    if (!state.dragging) return;

    const dy = e.clientY - state.startY;

    if (dy <= -FLICK_DISTANCE) {
      setOpen(true);
    } else if (dy >= FLICK_DISTANCE) {
      setOpen(false);
    } else {
      setOpen((dragOffset ?? state.startOffset) < state.closedOffset / 2);
    }

    setDragOffset(null);
  };

  // Position goes through a CSS variable so the hint keyframes can nudge relative to it
  let sheetY = '0px';
  if (dragOffset !== null) {
    sheetY = `${dragOffset}px`;
  } else if (!open) {
    sheetY = `calc(100% - ${peekHeight}px - env(safe-area-inset-bottom, 0px))`;
  }

  const handle = isPanel ? null : (
    <button
      type="button"
      className="sheet__handle"
      aria-expanded={open}
      aria-label={intl.formatMessage({ id: open ? 'hide_trip_details' : 'show_trip_details' })}
      onClick={() => setOpen(!open)}
    >
      <span className="sheet__handleBar" />
    </button>
  );

  return (
    <>
      {!isPanel && (
        <div
          className={`sheet__scrim${open ? ' sheet__scrim--visible' : ''}`}
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      <div
        ref={sheetRef}
        className={[
          'sheet',
          isPanel ? 'sheet--panel' : '',
          isOpen ? 'sheet--open' : '',
          dragOffset !== null ? 'sheet--dragging' : '',
          hinting ? 'sheet--hint' : '',
        ].join(' ')}
        style={{ '--sheet-y': sheetY } as React.CSSProperties}
        onAnimationEnd={onHintEnd}
      >
        <BottomSheetContent
          peekRef={peekRef}
          handle={handle}
          grabHandlers={isPanel ? {} : {
            onPointerDown,
            onPointerMove,
            onPointerUp: endDrag,
            onPointerCancel: endDrag,
          }}
        />
      </div>
    </>
  );
};

export default BottomSheet;
